import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, ToggleButton, ToggleButtonGroup,
  TextField, MenuItem, Grid, Card, CardContent,
  Chip, CircularProgress,
} from '@mui/material';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';

const COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#0288d1'];

// ── 기간 선택 헤더 ──────────────────────────────────────────────────
const PeriodSelector = ({ mode, setMode, year, setYear }) => {
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
      <ToggleButtonGroup
        size="small"
        value={mode}
        exclusive
        onChange={(_, v) => v && setMode(v)}
      >
        <ToggleButton value="monthly">월별</ToggleButton>
        <ToggleButton value="yearly">연도별 (2개년)</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'monthly' && (
        <TextField
          select size="small" label="연도" value={year}
          onChange={(e) => setYear(e.target.value)}
          sx={{ width: 110 }}
        >
          {years.map((y) => (
            <MenuItem key={y} value={String(y)}>{y}년</MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
};

// ── 요약 카드 ──────────────────────────────────────────────────────
const SummaryCards = ({ items }) => (
  <Grid container spacing={1.5} sx={{ mb: 2 }}>
    {items.map(({ label, value, color, unit }) => (
      <Grid item xs={6} sm={4} md={2} key={label}>
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color }}>
              {value}{unit}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// ── 메인 템플릿 ────────────────────────────────────────────────────
const KpiReportTemplate = ({
  title,
  subtitle,      // 선택. 제목 아래에 표시할 설명 문구(지표 기준 등 안내용)
  fetchFn,
  chartConfig,   // { type: 'bar'|'line', bars: [{key, name, color}] }
  columns,       // DataGrid columns
  summaryFn,     // (list) => [{label, value, color, unit}]
  xKey = 'kpiYm',
}) => {
  const currentYear = String(new Date().getFullYear());
  const [mode, setMode]   = useState('monthly');
  const [year, setYear]   = useState(currentYear);
  const [data, setData]   = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = mode === 'monthly'
        ? { searchYear: year }
        : { kpiYear: 'Y', searchYear: '' }; // yearly mode: no year filter to get all
      const res = await fetchFn(params);
      setData(res.data.list || []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [fetchFn, mode, year]);

  useEffect(() => { load(); }, [load]);

  // 차트 데이터: 월별은 kpiYm별 집계, 연도별은 kpiYear별 집계
  const chartData = React.useMemo(() => {
    const groupKey = mode === 'yearly' ? 'kpiYear' : 'kpiYm';
    const map = {};
    data.forEach((row) => {
      const key = row[groupKey];
      if (!key) return;
      if (!map[key]) map[key] = { [groupKey]: key };
      chartConfig.bars.forEach(({ key: barKey }) => {
        map[key][barKey] = (map[key][barKey] || 0) + (Number(row[barKey]) || 0);
      });
    });
    return Object.values(map).sort((a, b) => (a[groupKey] > b[groupKey] ? 1 : -1));
  }, [data, mode, chartConfig]);

  const xAxisKey = mode === 'yearly' ? 'kpiYear' : 'kpiYm';
  const summaryItems = summaryFn ? summaryFn(data) : [];

  const ChartComponent = chartConfig.type === 'line' ? LineChart : BarChart;
  const SeriesComponent = chartConfig.type === 'line' ? Line : Bar;

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: subtitle ? 0.5 : 2 }}>{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      <PeriodSelector mode={mode} setMode={setMode} year={year} setYear={setYear} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {summaryItems.length > 0 && <SummaryCards items={summaryItems} />}

          {/* 차트 */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <ChartComponent data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={xAxisKey} tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  {chartConfig.bars.map(({ key: barKey, name, color }, i) => (
                    <SeriesComponent
                      key={barKey}
                      type="monotone"
                      dataKey={barKey}
                      name={name}
                      fill={color || COLORS[i]}
                      stroke={color || COLORS[i]}
                      barSize={chartConfig.bars.length > 3 ? 10 : 20}
                    />
                  ))}
                </ChartComponent>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* 테이블 */}
          <DataGrid
            rows={data.map((r, i) => ({ id: i, ...r }))}
            columns={columns}
            autoHeight
            disableColumnMenu
            pageSizeOptions={[20, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
            density="compact"
          />
        </>
      )}
    </Box>
  );
};

export default KpiReportTemplate;
